import _CircularProgress from "@mui/material/CircularProgress";
import _Divider from "@mui/material/Divider";
import _Typography from "@mui/material/Typography";
import _FormControlLabel from "@mui/material/FormControlLabel";
import _Checkbox from "@mui/material/Checkbox";
import _Autocomplete from "@mui/material/Autocomplete";
import _Box from "@mui/material/Box";
import _FormControl from "@mui/material/FormControl";
import _Select from "@mui/material/Select";
import _MenuItem from "@mui/material/MenuItem";
import _TextField from "@mui/material/TextField";
import _InputAdornment from "@mui/material/InputAdornment";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Button2 from "@mui/material/Button";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyStateIllustration from '../../../assets/images/find-illus.svg';
import { useCancellablePromise } from '../../../hooks';
import { CyclabilityZoneService, EventService } from '../../../services';
import { SearchIcon } from '../../icons';
import { leaderboardSizes, leaderboardSizesMap } from '../sizes';
import LeaderboardCompanyItem from './item';
import RankCard from './rank-card';
function CompaniesLeaderboard({
  Button: _Button,
  event,
  ownGeogroup,
  selectedLeaderboardSize,
  search,
  selectedZone,
  sitesDisplayed,
  setSearch,
  selectSize,
  selectZone,
  displaySites,
  onOwnCompanyClick
}) {
  const [initialized, setInitialized] = useState(false);
  const [tmpSearch, setTmpSearch] = useState(search);
  const [cyclabilityZoneSearch, setCyclabilityZoneSearch] = useState('');
  const [cyclabilityZoneSearchResults, setCyclabilityZoneSearchResults] = useState();
  const [page, setPage] = useState(1);
  const [leaderboard, setLeaderboard] = useState();
  const [count, setCount] = useState();
  const [ownItem, setOwnItem] = useState();
  const [loading, setLoading] = useState(false);
  const locked = useRef(false);
  const fetch = useMemo(() => debounce(async (search, callback) => {
    if (locked.current) {
      callback([]);
      locked.current = false;
      return;
    }
    const {
      zones: cyclabilityZones
    } = await CyclabilityZoneService.getZones({
      administrativeLevels: ['CITY', 'EPCI', 'DEPARTMENT', 'REGION'],
      query: '{id, administrative_level, name}',
      search,
      rowsPerPage: 20,
      getAllItems: false
    });
    callback(cyclabilityZones);
  }, 400), []);
  const {
    t
  } = useTranslation();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  useEffect(() => {
    if (ownGeogroup !== undefined) setInitialized(true);
  }, [ownGeogroup]);
  useEffect(() => {
    if (initialized) {
      if (page > 1) setPage(1);
      getLeaderboard(1);
    }
    return () => {
      cancelPromises();
      setLeaderboard(undefined);
      setCount(undefined);
      setOwnItem(undefined);
    };
  }, [initialized, search, selectedLeaderboardSize, selectedZone, sitesDisplayed]);
  useEffect(() => {
    if (page > 1) getLeaderboard(page);
  }, [page]);
  useEffect(() => {
    let active = true;
    setCyclabilityZoneSearchResults(undefined);
    if (!cyclabilityZoneSearch || cyclabilityZoneSearch.length < 3) return;
    fetch(cyclabilityZoneSearch, cyclabilityZones => {
      if (active) setCyclabilityZoneSearchResults(cyclabilityZones);
    });
    return () => {
      active = false;
    };
  }, [cyclabilityZoneSearch, fetch]);
  async function getLeaderboard(page) {
    setLoading(true);
    try {
      const size = selectedLeaderboardSize ? selectedLeaderboardSize === 'small' ? {
        max: 50
      } : selectedLeaderboardSize === 'medium' ? {
        min: 51,
        max: 500
      } : {
        min: 501
      } : undefined;
      const isOwnGeogroupSize = ownGeogroup && (!selectedLeaderboardSize || selectedLeaderboardSize === ownGeogroup.companySize) && !sitesDisplayed;
      const [{
        count,
        items
      }, {
        items: [ownItem]
      }] = await cancellablePromise(Promise.all([EventService.getRanking(event.id, {
        page,
        pageSize: 50,
        groupTypes: ['company'],
        size,
        search,
        showSites: sitesDisplayed,
        cyclabilityZoneId: selectedZone ? selectedZone.id : undefined
      }), isOwnGeogroupSize && page === 1 ? EventService.getRanking(event.id, {
        page,
        pageSize: 1,
        groupTypes: ['company'],
        size,
        search,
        showSites: sitesDisplayed,
        cyclabilityZoneId: selectedZone ? selectedZone.id : undefined,
        groupsIds: [ownGeogroup.id]
      }) : {
        items: []
      }]));
      setCount(count);
      if (page === 1) {
        setLeaderboard(items);
        setOwnItem(ownItem || null);
      } else setLeaderboard([...(leaderboard || []), ...items]);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
    setLoading(false);
  }
  const size = selectedLeaderboardSize || 'none';
  const Button = _Button || _Button2;
  return /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: 3,
    sx: {
      containerType: 'inline-size'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "none",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setTmpSearch(value),
    onKeyDown: ({
      key
    }) => {
      if (key === 'Enter') setSearch(tmpSearch);
    },
    placeholder: t('commons.challenges.leaderboard.search_company'),
    size: "small",
    slotProps: {
      input: {
        endAdornment: tmpSearch && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_Tooltip, {
          title: "Rechercher"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
          disabled: !tmpSearch,
          onClick: () => setSearch(tmpSearch),
          size: "small"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })))))
      }
    },
    sx: {
      '.MuiInputBase-root': {
        bgcolor: '#fff'
      }
    },
    type: "text",
    value: tmpSearch,
    variant: "outlined"
  }), (selectSize || selectZone || displaySites) && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: {
      '@': 'column',
      '@500': 'row'
    },
    gap: 1
  }, selectSize && /*#__PURE__*/React.createElement(_Box, {
    width: {
      '@': '100%',
      '@500': 'calc((100% - 8px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    margin: "none"
  }, /*#__PURE__*/React.createElement(_Select, {
    onChange: ({
      target: {
        value
      }
    }) => selectSize(value === 'none' ? null : value),
    size: "small",
    value: size
  }, /*#__PURE__*/React.createElement(_MenuItem, {
    value: "none"
  }, t('commons.challenges.leaderboard.label_companies_all')), leaderboardSizes.map(key => /*#__PURE__*/React.createElement(_MenuItem, {
    key: key,
    value: key
  }, t(leaderboardSizesMap[key].labelKey)))))), selectZone && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    borderRadius: 2,
    display: "flex",
    width: {
      '@': '100%',
      '@500': 'calc((100% - 8px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_Autocomplete, {
    disablePortal: true,
    fullWidth: true,
    includeInputInList: true,
    forcePopupIcon: Boolean(cyclabilityZoneSearch && !selectedZone),
    getOptionKey: option => option.id,
    getOptionLabel: option => option.name,
    isOptionEqualToValue: (option, value) => option.id === value.id,
    loading: Boolean(cyclabilityZoneSearch) && !selectedZone,
    loadingText: t('commons.loading'),
    noOptionsText: cyclabilityZoneSearch && t('commons.no_result_found'),
    onChange: (_, value) => {
      locked.current = true;
      selectZone(value || null);
    },
    onInputChange: (_, value) => setTimeout(() => setCyclabilityZoneSearch(value), 10),
    options: cyclabilityZoneSearchResults || [],
    renderInput: params => /*#__PURE__*/React.createElement(_TextField, _extends({}, params, {
      fullWidth: true,
      margin: "none",
      placeholder: t('commons.challenges.leaderboard.search_zone'),
      size: "small",
      variant: "outlined"
    })),
    value: selectedZone
  })), displaySites && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: 2,
    display: "flex",
    width: {
      '@': '100%',
      '@500': 'calc((100% - 8px) / 3)'
    }
  }, /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    component: "fieldset",
    margin: "none"
  }, /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: sitesDisplayed,
      color: "primary",
      name: "sendDataToPartner",
      onChange: (_, checked) => displaySites(checked),
      size: "small",
      value: sitesDisplayed
    }),
    label: t('commons.challenges.leaderboard.display_sites'),
    sx: {
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: 0.5,
      paddingRight: 2
    }
  }))))), /*#__PURE__*/React.createElement(RankCard, {
    challengeTemplate: event.challengeTemplate,
    numberOfParticipants: count,
    progress: ownItem
  }), !leaderboard || leaderboard.length > 0 ? /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #dde0e7",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#F6F8FC",
    display: "flex",
    gap: 1,
    justifyContent: "flex-start",
    paddingX: 2,
    paddingY: 1,
    sx: {
      textAlign: 'left',
      textTransform: 'initial',
      color: 'initial'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0,
    width: 30
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexGrow: 1,
    gap: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "body2"
  }, t('commons.communities.form.types.company'))), /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0,
    width: 100
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700,
    variant: "body2"
  }, t('commons.challenge_details.table.participants'))), /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0,
    width: 100
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700,
    variant: "body2"
  }, t('commons.challenge_details.table.average_per_participant'))), /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0,
    width: 80
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "right",
    fontWeight: 700,
    variant: "body2"
  }, t('commons.challenge_details.table.performance', {
    context: event.challengeTemplate?.type === 'bttf' ? 'bttf' : event.challengeTemplate?.type === 'custom' ? 'custom' : event.challengeTemplate?.type === 'traveledDistance' ? 'distance' : event.challengeTemplate?.type === 'cyclingDays' ? 'days' : event.challengeTemplate?.type === 'homeWorkJourneysCount' || event.challengeTemplate?.type === 'journeysCount' ? 'journeys' : ''
  })))), leaderboard ? leaderboard.map((item, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: item.id
  }, /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardCompanyItem, {
    enableParticipants: true,
    challengeType: event.challengeTemplate?.type,
    item: item,
    onOwnCompanyClick: onOwnCompanyClick,
    ownGeogroup: ownGeogroup,
    prevItemRank: index > 0 ? leaderboard[index - 1].rank : null
  }))) : new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardCompanyItem, {
    enableParticipants: true,
    challengeType: event.challengeTemplate?.type,
    prevItemRank: null
  })))) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 5
  }, /*#__PURE__*/React.createElement("img", {
    height: 64,
    src: emptyStateIllustration
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700
  }, t('commons.challenges.leaderboard.empty_state_title_company')), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    variant: "body2"
  }, t('commons.challenges.leaderboard.empty_state_description')))), leaderboard && count && count > leaderboard.length ? /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: loading,
    endIcon: loading && /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }),
    onClick: () => setPage(page + 1),
    size: "small",
    variant: "outlined"
  }, t('commons.actions.see_more'))) : /*#__PURE__*/React.createElement(React.Fragment, null));
}
export default CompaniesLeaderboard;